/**
 * M4.V2 — the kanban board view (`specs/M4-V2-kanban.md`).
 *
 * The whole read is B4 §8.8's grouped board: `groupBy = { columnId: pivot, showEmptyGroups }`
 * returns the pivot column's vocabulary as virtual groups, coloured, counted and paged, and
 * this paints them sideways. Dragging a card writes one cell through the cells service, so
 * activity, automations and realtime fire exactly as they do in the table.
 *
 * Drag is **dnd-kit** (ADR-001), added in round 5x: a card is a `useDraggable`, a lane is a
 * `useDroppable`, the preview is a `<DragOverlay>` and `KeyboardSensor` gives the gesture the
 * keyboard alternative the native API could not (q405). `lanes.ts` still owns the arithmetic —
 * the round-5u decision D-V2-2 said a later round would replace the callers, not the logic,
 * and that is exactly what this is.
 */
import {
  DndContext,
  DragOverlay,
  useDraggable,
  useDroppable,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { KANBAN_METRICS, kanbanCardHeight, laneHeaderColor, selectedColumnIds, type KanbanViewSettings } from '@elizmonday/shared';
import { PageEmptyState, PageSkeleton } from '@elizmonday/ui';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import type { BoardColumn, BoardDataSource, ItemRow } from '../../board-view/data/types';
import type { CellRenderContext } from '../../board-view/columns/types';
import { ItemCardTile } from '../shared/ItemCardTile';
import { coverColumnOf, coverImageFor } from '../shared/cover';
import { laneKeyboardCoordinates, useViewDragSensors, viewCollisionDetection } from '../shared/dnd';
import { useViewData } from '../shared/use-view-data';
import { useViewSettings, type ViewSettingsOverride } from '../shared/use-view-settings';
import { ViewToolbar } from '../shared/ViewToolbar';
import { BoardViewToolbar, useViewQueryControls } from '../shared/BoardViewToolbar';
import {
  alreadyInLane,
  canDragOnPivot,
  defaultPivotColumnId,
  laneDropValue,
  lanesOf,
  pivotCandidates,
  type Lane,
} from './lanes';
import './kanban.css';

export interface KanbanViewProps {
  boardId: string;
  viewId: string;
  source: BoardDataSource;
  /** The `/__ui/views` harness supplies its own settings; the product route does not. */
  settingsOverride?: ViewSettingsOverride<KanbanViewSettings>;
}

export function KanbanView({ boardId, viewId, source, settingsOverride }: KanbanViewProps) {
  const handle = useViewSettings<KanbanViewSettings>(boardId, viewId, 'kanban', settingsOverride);
  const settings = handle.settings;
  const navigate = useNavigate();
  const [dragging, setDragging] = useState<string | null>(null);
  const [overLane, setOverLane] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pending = useRef(new Set<string>());

  /**
   * A view whose pivot was never set — every kanban created through our own gallery — picks
   * the first groupable column rather than rendering an error (`[V2§4]`).
   */
  const probe = useViewData({ source, boardId, viewId });
  const columns = probe.payload?.columns ?? [];
  const candidates = useMemo(() => pivotCandidates(columns), [columns]);
  /* P-025: the fallback is the board's first **status** column, never the first bucketable one. */
  const pivotColumnId = settings.pivotColumnId ?? defaultPivotColumnId(columns);

  /**
   * P-025: the board toolbar every non-table view wears. It owns the search, the person
   * shortcut, the quick filter and the sort, and hands them back as `QueryState` — so a filter
   * set on the kanban is the same server-side read the grid does.
   */
  const toolbar = useViewQueryControls();

  const data = useViewData({
    source,
    boardId,
    viewId,
    state: pivotColumnId
      ? { ...toolbar.state, groupBy: { columnId: pivotColumnId as never, direction: 'asc', showEmptyGroups: true } }
      : toolbar.state,
  });

  const pivotColumn = columns.find((column) => column.id === pivotColumnId);
  const cardColumns = useMemo(
    () => resolveCardColumns(columns, settings),
    [columns, settings],
  );
  const coverColumn = useMemo(
    () => coverColumnOf(columns, selectedColumnIds(settings.coverColumns, columns.map((column) => column.id))),
    [columns, settings.coverColumns],
  );
  const lanes = useMemo(
    () => lanesOf(data.payload?.groups ?? [], settings.collapsedLaneKeys),
    [data.payload, settings.collapsedLaneKeys],
  );
  const draggable = canDragOnPivot(pivotColumn);

  const openItem = useCallback(
    (itemId: string) => navigate(`/boards/${boardId}/views/${viewId}/pulses/${itemId}`),
    [navigate, boardId, viewId],
  );

  const drop = useCallback(
    (lane: Lane, itemId: string) => {
      if (pivotColumn === undefined) return;
      const item = data.allRows().find((row) => row.id === itemId);
      if (item === undefined) return;
      if (alreadyInLane(pivotColumn, item, lane.bucketKey)) return;
      const value = laneDropValue(pivotColumn, item, lane.bucketKey);
      if (value === undefined) return;
      pending.current.add(itemId);
      void source
        .setCellValues({
          boardId: boardId as never,
          writes: [{ itemId: itemId as never, columnId: pivotColumn.id, value }],
        })
        .then(() => {
          setError(null);
          data.refresh();
        })
        .catch((reason: unknown) => {
          setError(reason instanceof Error ? reason.message : 'The card could not be moved');
          data.refresh();
        })
        .finally(() => pending.current.delete(itemId));
    },
    [pivotColumn, data, source, boardId],
  );

  /* ── dnd-kit wiring (`shared/dnd.ts`) ─────────────────────────────────── */

  /**
   * **P-142.** The keyboard half of the card drag. The `KeyboardSensor` and the card's
   * `tabIndex` / `aria-roledescription` were already here, but the sensor had no way to reach a
   * *lane* — dnd-kit's default getter moves 25 px per arrow and a lane is 280 wide — so Space,
   * two arrows and Space wrote nothing at all. `laneKeyboardCoordinates` walks the lane list and
   * `viewCollisionDetection` keeps `pointerWithin` for the pointer while giving the keyboard a
   * rectangle algorithm, which is what turns the arrow press into the same `event.over` a
   * pointer drop produces. The write path below is untouched.
   */
  const sensors = useViewDragSensors(laneKeyboardCoordinates);
  const laneById = useMemo(() => new Map(lanes.map((lane) => [lane.id, lane])), [lanes]);
  const draggedRow = useMemo(
    () => (dragging === null ? null : (data.allRows().find((row) => row.id === dragging) ?? null)),
    [dragging, data],
  );

  const onDragStart = useCallback((event: DragStartEvent) => setDragging(String(event.active.id)), []);
  const onDragOver = useCallback(
    (event: DragOverEvent) => setOverLane(event.over === null ? null : String(event.over.id)),
    [],
  );
  const onDragCancel = useCallback(() => {
    setDragging(null);
    setOverLane(null);
  }, []);
  const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      setDragging(null);
      setOverLane(null);
      const lane = event.over === null ? undefined : laneById.get(String(event.over.id));
      if (lane !== undefined) drop(lane, String(event.active.id));
    },
    [laneById, drop],
  );

  const toggleLane = useCallback(
    (lane: Lane) => {
      const next = lane.collapsed
        ? settings.collapsedLaneKeys.filter((key) => key !== lane.bucketKey)
        : [...settings.collapsedLaneKeys, lane.bucketKey];
      handle.save({ collapsedLaneKeys: next });
    },
    [handle, settings.collapsedLaneKeys],
  );

  if (!handle.ready || probe.pending) return <PageSkeleton variant="board" />;
  if (probe.error) {
    return <PageEmptyState title="This board could not be read" hint={probe.error.message} />;
  }
  if (pivotColumnId === null) {
    return (
      <div className="ez-kanban" data-testid="kanban-view">
        <PageEmptyState
          title="Kanban needs a column to make lanes from"
          hint="Add a status, dropdown or people column to this board and it will appear here."
          data-testid="kanban-no-pivot"
        />
      </div>
    );
  }

  return (
    <div className="ez-kanban" data-testid="kanban-view" data-pivot={pivotColumnId}>
      <BoardViewToolbar
        controls={toolbar.controls}
        boardId={boardId}
        viewId={viewId}
        registryId="kanban"
        source={source}
        payload={probe.payload}
        /* The lanes are virtual `gb:` groups; `New item` writes to the board's real first one. */
        groups={probe.payload?.groups ?? []}
        onChanged={() => {
          probe.refresh();
          data.refresh();
        }}
        gear={
        <ViewToolbar
          testId="kanban-toolbar"
          title="Kanban"
          fields={[
            {
              kind: 'select',
              id: 'pivot_by_column',
              label: 'Kanban Column',
              value: pivotColumnId,
              options: candidates.map((column) => ({ value: column.id, label: column.title })),
              onChange: (value) => handle.save({ pivotColumnId: value }),
            },
            {
              kind: 'columns',
              id: 'card_columns',
              label: 'Card Columns',
              columns: columns.map((column) => ({ id: column.id, title: column.title, type: column.type })),
              selection: settings.cardColumns,
              onChange: (selection) => handle.save({ cardColumns: selection }),
            },
            {
              kind: 'columns',
              id: 'gallery_column',
              label: 'Cover Columns',
              columns: columns.map((column) => ({ id: column.id, title: column.title, type: column.type })),
              selection: settings.coverColumns,
              onChange: (selection) => handle.save({ coverColumns: selection }),
            },
            {
              kind: 'radio',
              id: 'display_mode',
              label: 'Cover display mode',
              value: settings.coverDisplayMode,
              options: [
                { value: 'crop', label: 'Crop' },
                { value: 'fit', label: 'Fit' },
              ],
              onChange: (value) => handle.save({ coverDisplayMode: value as 'crop' | 'fit' }),
            },
          ]}
        />
        }
      />

      {error === null ? null : (
        <div className="ez-kanban__error" role="alert" data-testid="kanban-error">
          {error}
        </div>
      )}

      {data.pending && data.payload === null ? (
        <PageSkeleton variant="board" />
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={viewCollisionDetection}
          onDragStart={onDragStart}
          onDragOver={onDragOver}
          onDragEnd={onDragEnd}
          onDragCancel={onDragCancel}
        >
          <div className="ez-kanban__strip" role="list" data-testid="kanban-strip">
            {lanes.map((lane) => (
              <KanbanLane
                key={lane.id}
                lane={lane}
                rows={data.rowsOf(lane.id)}
                cardColumns={cardColumns}
                coverColumn={coverColumn}
                coverDisplayMode={settings.coverDisplayMode}
                context={data.context}
                draggable={draggable}
                dragging={dragging}
                over={overLane === lane.id}
                onOpen={openItem}
                onToggle={() => toggleLane(lane)}
                onLoadMore={() => data.loadMore(lane.id)}
              />
            ))}
          </div>

          {/* The preview monday shows: the card itself, not the browser's screenshot of it. */}
          <DragOverlay dropAnimation={null}>
            {draggedRow === null ? null : (
              <ItemCardTile
                item={draggedRow}
                columns={cardColumns}
                context={data.context}
                size="kanban"
                cover={coverImageFor(draggedRow, coverColumn, data.context, settings.coverDisplayMode)}
                testIdPrefix="kanban-drag-preview"
                onOpen={() => undefined}
              />
            )}
          </DragOverlay>
        </DndContext>
      )}
    </div>
  );
}

/** The card's cell rows: the configured columns, minus the pivot itself (it is the lane). */
function resolveCardColumns(
  columns: readonly BoardColumn[],
  settings: KanbanViewSettings,
): BoardColumn[] {
  const ids = new Set(selectedColumnIds(settings.cardColumns, columns.map((column) => column.id)));
  if (settings.assigneeColumnId !== null) ids.add(settings.assigneeColumnId);
  return columns.filter((column) => column.id !== 'name' && ids.has(column.id));
}

interface KanbanLaneProps {
  lane: Lane;
  rows: readonly ItemRow[];
  cardColumns: readonly BoardColumn[];
  coverColumn: BoardColumn | undefined;
  coverDisplayMode: 'crop' | 'fit';
  context: CellRenderContext;
  draggable: boolean;
  dragging: string | null;
  over: boolean;
  onOpen(itemId: string): void;
  onToggle(): void;
  onLoadMore(): void;
}

function KanbanLane(props: KanbanLaneProps) {
  const { lane, rows, cardColumns, context } = props;
  const cardHeight = kanbanCardHeight(cardColumns.length, props.coverColumn !== undefined);
  /**
   * The droppable is the **whole lane**, collapsed one included: monday drops on the column,
   * not on a gap between two cards, and `[V2§7.3]` reorders within a lane only when the lane
   * is a real group — which it never is under a pivot.
   */
  const { setNodeRef } = useDroppable({ id: lane.id, disabled: !props.draggable });

  return (
    <section
      ref={setNodeRef}
      className="ez-kanban__lane"
      role="listitem"
      data-testid={`kanban-lane-${lane.bucketKey}`}
      data-collapsed={lane.collapsed || undefined}
      data-over={props.over || undefined}
      aria-label={`${lane.title}, ${String(lane.count)} items`}
      style={{ width: lane.collapsed ? KANBAN_METRICS.laneHeaderHeight : KANBAN_METRICS.laneWidth }}
    >
      <button
        type="button"
        className="ez-kanban__lane-header"
        style={{ backgroundColor: laneHeaderColor(lane.color) }}
        data-testid={`kanban-lane-header-${lane.bucketKey}`}
        onClick={props.onToggle}
        aria-expanded={!lane.collapsed}
      >
        <span className="ez-kanban__lane-name">{lane.title}</span>
        <span className="ez-kanban__lane-count">{`/ ${String(lane.count)}`}</span>
      </button>

      {lane.collapsed ? null : (
        <div
          className="ez-kanban__lane-body"
          data-testid={`kanban-lane-body-${lane.bucketKey}`}
          onScroll={(event) => {
            const node = event.currentTarget;
            if (node.scrollTop + node.clientHeight >= node.scrollHeight - cardHeight) props.onLoadMore();
          }}
        >
          {rows.map((item) => (
            <KanbanCard
              key={item.id}
              item={item}
              columns={cardColumns}
              context={context}
              cover={coverImageFor(item, props.coverColumn, context, props.coverDisplayMode)}
              draggable={props.draggable}
              dragging={props.dragging === item.id}
              onOpen={props.onOpen}
            />
          ))}
        </div>
      )}
    </section>
  );
}

/**
 * One draggable card. The listeners go on the card itself (it is the affordance monday uses,
 * `[V2§7.3]`), and `distance: 5` on the pointer sensor is what keeps the title button
 * clickable through them.
 */
function KanbanCard(props: {
  item: ItemRow;
  columns: readonly BoardColumn[];
  context: CellRenderContext;
  cover: { url: string; fit: 'cover' | 'contain' } | null;
  draggable: boolean;
  dragging: boolean;
  onOpen(itemId: string): void;
}) {
  const { attributes, listeners, setNodeRef } = useDraggable({
    id: props.item.id,
    disabled: !props.draggable,
  });
  // `useDraggable` labels its node `role="button"`; this one is an `<article>` that *contains*
  // buttons, so the role is dropped and the rest of the ARIA (roledescription, describedby,
  // tabIndex — what the keyboard sensor needs) is kept.
  const { role: _role, ...aria } = attributes;

  return (
    <ItemCardTile
      item={props.item}
      columns={props.columns}
      context={props.context}
      size="kanban"
      cover={props.cover}
      draggable={props.draggable}
      dragRef={setNodeRef}
      dragProps={props.draggable ? { ...aria, ...listeners } : undefined}
      testIdPrefix="kanban-card"
      // The dragged card fades to 0.4 and the overlay carries the real one (§7.3).
      style={{ opacity: props.dragging ? 0.4 : undefined }}
      onOpen={props.onOpen}
    />
  );
}
